import { ChangeEvent, Dispatch, SetStateAction } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Paper, PaperProps, TextField, Typography } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { t } from '@lingui/core/macro'

const marginTop = css`
  margin-top: 24px;
`

const UniqueValuesContainer = styled(motion(Paper))<PaperProps>`
  margin-top: 16px;
  padding: 12px;
  text-align: center;
`

const UniqueValues = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`

const MIN_INPUT_VALUE = 1
const MAX_BIG_INT = 2 ** 64
const MAX_WIDTH = MAX_BIG_INT.toString().length

interface Props {
  minWidth: number
  startValue: number
  updater: Dispatch<SetStateAction<any>>
}

export const AutonumberDetails = ({ startValue, minWidth, updater }: Props) => {
  const startValueLength = String(startValue).length
  const maxValue = 10 ** +minWidth - 1
  const numberOfUniqueValues = maxValue - +startValue + 1
  const startValueErrorMessage = getStartValueErrorMessage(startValue)
  const minWidthErrorMessage = getMinWidthErrorMessage(minWidth, startValueLength)
  const showNumberOfUniqueValues = !startValueErrorMessage && !minWidthErrorMessage

  return (
    <>
      <div css={marginTop}>
        <TextField
          required
          name="start-with"
          InputProps={{
            inputProps: {
              min: MIN_INPUT_VALUE,
              max: MAX_BIG_INT,
            },
          }}
          variant="outlined"
          fullWidth
          type="number"
          label={t`Start with`}
          value={startValue}
          onChange={handleChangeStartValue}
          error={Boolean(startValueErrorMessage)}
          helperText={startValueErrorMessage}
        />
      </div>
      <div css={marginTop}>
        <TextField
          required
          name="min-width"
          InputProps={{
            inputProps: {
              min: startValueLength,
              max: MAX_BIG_INT.toString().length,
            },
          }}
          variant="outlined"
          fullWidth
          type="number"
          label={t`Min width`}
          value={minWidth}
          onChange={handleChangeMinWidth}
          error={Boolean(minWidthErrorMessage)}
          helperText={minWidthErrorMessage}
        />
      </div>
      <AnimatePresence>
        {showNumberOfUniqueValues && (
          <UniqueValuesContainer
            key="autonumberUniqueValues"
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div>
              <UniqueValues>
                <Typography variant="body1">Number of unique values:&nbsp;</Typography>
                <Typography variant="body1" color="primary">
                  {numberOfUniqueValues.toLocaleString()}
                </Typography>
              </UniqueValues>
              <Typography variant="caption">
                ({startValue} - {maxValue})
              </Typography>
            </div>
          </UniqueValuesContainer>
        )}
      </AnimatePresence>
    </>
  )

  function handleChangeStartValue(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const { value } = e.target

    updater(prevState => {
      const updatedStartValue = +value < 0 ? MIN_INPUT_VALUE : value
      const updatedMinWidth =
        value.length > minWidth ? value.length : prevState.autonumber.min_width

      return {
        ...prevState,
        autonumber: {
          ...prevState.autonumber,
          start_value: updatedStartValue,
          min_width: updatedMinWidth,
        },
      }
    })
  }

  function handleChangeMinWidth(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const { value } = e.target
    const updatedMinWidth = +value < 0 ? MIN_INPUT_VALUE : value

    updater(prevState => ({
      ...prevState,
      autonumber: {
        ...prevState.autonumber,
        min_width: updatedMinWidth,
      },
    }))
  }
}

function getMinWidthErrorMessage(minWidth: number, startValueLength: number) {
  if (!minWidth) {
    return 'Value is required'
  }

  if (minWidth < startValueLength) {
    return `Value must be between ${startValueLength} and ${MAX_WIDTH}`
  }

  if (minWidth > MAX_WIDTH) {
    return `Value must be less than or equal to ${MAX_WIDTH}`
  }

  return ''
}

function getStartValueErrorMessage(startValue: number) {
  if (!startValue) {
    return 'Value is required'
  }

  if (startValue.toString().length > MAX_WIDTH) {
    return `Value must be less than or equal to ${MAX_BIG_INT.toLocaleString()}`
  }

  return ''
}
